// Client-side PDF/Word export for Reports, mirroring BillProfile.js's
// downloadAnalysisAsPDF/downloadAnalysisAsWord pattern (browser print-to-PDF +
// HTML-as-.doc blob) — no new npm dependencies. Report bill entries here are always
// plain text (never linked to BillProfile), since a static export can't carry auth state.

import DOMPurify from 'dompurify';
import { deriveBillDisplayFields } from './billDisplay';
import { computeCalendarHeaders } from './billSortGrouping';

const REPORT_DOC_STYLES = `
    body, div { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; color: #1a2744; line-height: 1.5; }
    .report-header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #458da1; padding-bottom: 12px; margin-bottom: 18px; }
    .report-header img { max-height: 60px; max-width: 180px; object-fit: contain; }
    .report-header .org-name { font-size: 13pt; font-weight: bold; color: #1a2744; }
    .report-title { font-size: 15pt; font-weight: bold; margin-bottom: 14px; color: #1a2744; }
    .report-intro-message { font-size: 10.5pt; color: #2c3a42; border-left: 3px solid #458da1; background: #f4f8fa; padding: 10px 14px; margin-bottom: 16px; }
    .report-intro-message p { margin: 0; }
    .calendar-date-header { font-size: 12pt; font-weight: bold; color: #2c4a52; border-bottom: 1.5px solid #458da1; padding-bottom: 4px; margin: 16px 0 6px; }
    .calendar-location-subheader { font-size: 10.5pt; font-weight: bold; color: #2c4a52; text-transform: uppercase; letter-spacing: 0.05em; background: #ddedf2; padding: 4px 10px; margin: 6px 0; border-left: 4px solid #458da1; }
    .report-bill { border: 1px solid #e2edf1; border-left: 3px solid #71b4c7; border-radius: 6px; padding: 8px 12px; margin-bottom: 10px; page-break-inside: avoid; }
    .report-bill .bill-number { color: #458da1; font-weight: bold; }
    .report-bill .bill-title { font-size: 11pt; font-weight: bold; color: #1a2744; margin: 4px 0; }
    .report-bill .bill-meta { font-size: 9pt; color: #5a6a72; }
    .report-bill .bill-summary-content { font-size: 9.5pt; color: #5a6a72; margin-top: 6px; }
    .report-bill .report-bill-note { font-size: 9.5pt; color: #5a6a72; margin-top: 6px; }
`;

function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function renderBillBlock(bill, showDigest, showAiSummary) {
  const { billDisplay, authorName, location, status, fileLocationDisplay, hearingInfo } = deriveBillDisplayFields(bill);

  return `
    <div class="report-bill">
      <div><span class="bill-number">${escapeHtml(billDisplay)}</span> - ${escapeHtml(authorName)}</div>
      <div class="bill-title">${escapeHtml(bill.title)}</div>
      <div class="bill-meta">
        <strong>${escapeHtml(status)}</strong>${status === 'Chaptered' && bill.chapter_number && bill.chapter_year ? ` - Chapter ${escapeHtml(bill.chapter_number)}, Statutes of ${escapeHtml(bill.chapter_year)}` : ''}${status !== 'Chaptered' && location ? `: ${escapeHtml(location)}` : ''}
        ${fileLocationDisplay ? ` &nbsp;|&nbsp; ${escapeHtml(fileLocationDisplay)}` : ''}
        ${hearingInfo ? ` &nbsp;|&nbsp; Next Hearing: ${escapeHtml(hearingInfo)}` : ''}
      </div>
      ${showDigest && bill.summary ? `<div class="bill-summary-content"><u>Digest:</u> ${escapeHtml(bill.summary)}</div>` : ''}
      ${showAiSummary && bill.ai_summary ? `<div class="bill-summary-content"><u>AI Summary:</u> ${escapeHtml(bill.ai_summary.replace(/^#+\s+[^\n]*\n*/g, '').trim())}</div>` : ''}
      ${bill.report_note ? `<div class="report-bill-note"><u>Note:</u> ${escapeHtml(bill.report_note)}</div>` : ''}
    </div>`;
}

// Builds the shared report HTML body (letterhead header + title + bills), used by both
// the PDF and Word export paths.
function buildReportHtml(report, bills) {
  const calendarHeaders = computeCalendarHeaders(bills, report.sort_mode);

  const headerHtml = `
    <div class="report-header">
      ${report.org_logo ? `<img src="${escapeHtml(report.org_logo)}" alt="${escapeHtml(report.org_name || '')} logo" />` : ''}
      <div class="org-name">${escapeHtml(report.org_name || report.team_name || '')}</div>
    </div>`;

  const billsHtml = bills.map(bill => {
    const calendarInfo = calendarHeaders.get(bill.bill_id);
    const dateHeader = calendarInfo?.showDateHeader
      ? `<div class="calendar-date-header">${escapeHtml(calendarInfo.headerDateStr)}</div>` : '';
    const locHeader = calendarInfo?.showLocHeader
      ? `<div class="calendar-location-subheader">${escapeHtml(calendarInfo.headerLocStr)}</div>` : '';
    return `${dateHeader}${locHeader}${renderBillBlock(bill, report.show_digest, report.show_ai_summary)}`;
  }).join('');

  const introHtml = report.intro_message
    ? `<div class="report-intro-message">${DOMPurify.sanitize(report.intro_message)}</div>` : '';

  return `${headerHtml}<div class="report-title">${escapeHtml(report.title)}</div>${introHtml}${billsHtml}`;
}

export function downloadReportAsPDF(report, bills) {
  const html = buildReportHtml(report, bills);
  const win = window.open('', '_blank');
  win.document.write(`<html><head><title>${escapeHtml(report.title)}</title><style>${REPORT_DOC_STYLES} @media print { body { margin: 0.6in; } }</style></head><body style='margin:0.6in;'>${html}<script>window.onload = function () { window.print(); };</script></body></html>`);
  win.document.close();
  win.focus();
}
